/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Logger }                  from '@/util/logger';
import {IUserLogEntryOptions}      from './i.user.log.entry.options';
import {SimulationBuilder}         from './simulation.builder';
import {TrainBuilder}              from './train.builder';
import {MessageBuilder}            from './message.builder';
import {SummaryBuilder}            from './summary.builder';
import {DecisionPointGroupBuilder} from './decision.point.group.builder';
import {DispatchBuilder}           from './dispatch.builder';
import {DeploymentBuilder}         from './deployment.builder';
import {AuthorityBuilder}          from './authority.builder';
import {PossibleMovementsBuilder}  from './possible.movements.builder';
import {OptimizationBuilder}       from './optimization.builder';
import {DoubleTrackParkingBuilder} from './double.track.parking.builder';
import {SidingTrackParkingBuilder} from './siding.track.parking.builder';
import {TrainMovesBuilder}         from './train.moves.builder';
import {CanPassBuilder}            from './can.pass.builder';
import {CanParkBuilder}            from './can.park.builder';
import {CanPassIgnoreBuilder}      from './can.pass.ignore.builder';
import {CommitSnapshotBuilder}     from './commit.snapshot.builder';
import {AddLineupBuilder}          from './add.lineup.builder';
import {AddTrainBuilder}           from './add.train.builder';
import {ParkTrainBuilder}          from './park.train.builder';
import {FindSidingParkingBuilder}  from './find.siding.parking.builder';
import {BuildImpliedBuilder}       from './build.implied.builder';
import {AcceptedMovesBuilder}      from './accepted.moves.builder';
import {RejectedMovesBuilder}      from './rejected.moves.builder';
import {AcceptedPossibleBuilder}   from './accepted.possible.builder';
import {RejectedPossibleBuilder}   from './rejected.possible.builder';
import {AcceptedOptBuilder}        from './accepted.opt.builder';
import {RejectedOptBuilder}        from './rejected.opt.builder';
import {AcceptedSelectionBuilder}  from './accepted.selection.builder';
import {RejectedSelectionBuilder}  from './rejected.selection.builder';

/**
 *
 * @class UserLogNodeFactory
 *
 * @brief auto-construct nodes for the TreeModel tree in the user log component.  We
 * convert from the raw user log entries we get from MongoDB, into fully detailed
 * nodes that are usable in the tree presented to the user.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2021
 *
 */
export class UserLogNodeFactory {

  /** convert from MongoDB raw entries to tree nodes */
  public static build(data: any, options: any): any {

    const defaults: IUserLogEntryOptions = {
      index:       0,
      isExpanded:  false,
      hasChildren: false,
      children:    null,
      title:       '',
      parent:      null
    };

    const args       = Object.assign({}, defaults, options);

    args.hasChildren = ((data.ch != null) && (data.ch.length > 0)) || ((args.children !== null) && (args.children.length > 0));

    /* simulation */

    if (/^simulation/.test(data.lb)) {

      return new SimulationBuilder().build(data, args);
    }

    /* train */

    if (/^TRN-/.test(data._id)) {

      return new TrainBuilder().build(data, args);
    }

    if ((typeof data.type != 'undefined') && (data.type == 'accepted-moves')) {

      return new AcceptedMovesBuilder().build(data, args);
    }

    if ((typeof data.type != 'undefined') && (data.type == 'rejected-moves')) {

      return new RejectedMovesBuilder().build(data, args);
    }

    if ((typeof data.type != 'undefined') && (data.type == 'accepted-possible')) {

      return new AcceptedPossibleBuilder().build(data, args);
    }

    if ((typeof data.type != 'undefined') && (data.type == 'rejected-possible')) {

      return new RejectedPossibleBuilder().build(data, args);
    }

    if ((typeof data.type != 'undefined') && (data.type == 'accepted-authorities')) {

      return new AcceptedOptBuilder().build(data, args);
    }

    if ((typeof data.type != 'undefined') && (data.type == 'rejected-authorities')) {

      return new RejectedOptBuilder().build(data, args);
    }

    if ((typeof data.type != 'undefined') && (data.type == 'accepted-selections')) {

      return new AcceptedSelectionBuilder().build(data, args);
    }

    if ((typeof data.type != 'undefined') && (data.type == 'rejected-selections')) {

      return new RejectedSelectionBuilder().build(data, args);
    }

    /* message */

    if (/^msg-/.test(data._id)) {

      return new MessageBuilder().build(data, args);
    }

    /* (summary) message */

    if (/^sum-/.test(data._id)) {

      return new SummaryBuilder().build(data, args);
    }

    /* decision point grouping (time and place for a train) */

    if (/^dpg-/.test(data._id)) {

      return new DecisionPointGroupBuilder().build(data, args);
    }

    /* dispatch */

    if (/^dis-/.test(data._id)) {

      return new DispatchBuilder().build(data, args);
    }

    /* commit queue deployment */

    if (/^cc-snap-/.test(data._id)) {

      return new CommitSnapshotBuilder().build(data, args);
    }

    if (/^cc-\d+/.test(data._id)) {

      return new DeploymentBuilder().build(data, args);
    }

    /* handle departure events */

    if (/^cus-/.test(data._id) && (data.t == 'dpt')) {

      /*
       * these are custom events that just tell the train context what the actual
       * dispatches are, that is, the ones that actually got used to move the train.
       * this in turn can help the GUI highlight a path through three of which
       * branches were "actual" branches, rather than branches that were attempted,
       * but never actually used to move a train.
       *
       * We don't return a node here, we just build up the "actuals" in the train
       * parent.  These events get kicked out as children of the train context, so
       * the parent data is the train.
       *
       */

      args.parent.actualMoves.push(data);

      return null;
    }

    /* movement authority */

    if (/^cus-/.test(data._id) && (data.t == 'a')) {

      return new AuthorityBuilder().build(data, args);
    }

    if (/^mv-trains-/.test(data._id)) {

      return new TrainMovesBuilder().build(data, args);
    }

    /* possible moves */

    if (/^mv-/.test(data._id)) {

      return new PossibleMovementsBuilder().build(data, args);
    }

    if (/^sel-/.test(data._id)) {

      return new OptimizationBuilder().build(data, args);
    }

    if (/^double-track-park-/.test(data._id)) {

      return new DoubleTrackParkingBuilder().build(data, args);
    }

    if (/^siding-track-park-/.test(data._id)) {

      return new SidingTrackParkingBuilder().build(data, args);
    }

    if (/^find-siding-parking-/.test(data._id)) {

      return new FindSidingParkingBuilder().build(data, args);
    }

    if (/^can-pass-\d+/.test(data._id)) {

      return new CanPassBuilder().build(data, args);
    }

    if (/^can-pass-ignore/.test(data._id)) {

      return new CanPassIgnoreBuilder().build(data, args);
    }

    if (/^can-park-/.test(data._id)) {

      return new CanParkBuilder().build(data, args);
    }

    if (/^add-lineup-/.test(data._id)) {

      return new AddLineupBuilder().build(data, args);
    }

    if (/^park-train-/.test(data._id)) {

      return new ParkTrainBuilder().build(data, args);
    }

    if (/^add-train-/.test(data._id)) {

      return new AddTrainBuilder().build(data, args);
    }

    if (/^build-implied-/.test(data._id)) {

      return new BuildImpliedBuilder().build(data, args);
    }

    Logger.error(`[UserLogNodeFactory] unrecognized MonoDB entry: `, data);

    /* no match  */

    return  null;
  }

}
